// /src/components/ImageUpload.jsx
import React, { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon, Plus } from 'lucide-react'

const ImageUpload = ({ onImageUpload, currentImages = [], className = '', maxImages = 5, onUploadStateChange }) => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)

  // Convert single image to array for backward compatibility
  const images = Array.isArray(currentImages) ? currentImages : (currentImages ? [currentImages] : [])
  
  console.log('🖼️ ImageUpload received currentImages:', currentImages);
  console.log('🖼️ ImageUpload processed images:', images);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    // Check if adding these files would exceed the limit
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed. You can add ${maxImages - images.length} more.`)
      return
    }

    setIsUploading(true)
    setError('')
    setUploadProgress(0)
    
    // Notify parent component about upload state
    if (onUploadStateChange) {
      onUploadStateChange(true);
    }

    const uploadedUrls = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        setError(`File "${file.name}" is not a valid image file (JPEG, PNG, WebP, or GIF)`)
        continue
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        setError(`File "${file.name}" is too large. Maximum size is 5MB.`)
        continue
      }

      try {
        const formData = new FormData()
        formData.append('image', file)

        // Use the current domain for API calls
        const apiUrl = window.location.hostname === 'localhost' 
          ? '/api/upload' 
          : `${window.location.origin}/api/upload`

        console.log('🔄 Uploading to:', apiUrl)
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Upload failed')
        }

        const result = await response.json()
        console.log('✅ Upload result:', result)
        
        // Replace the R2 domain with your Worker proxy route
        const proxyUrl = result.url.replace(
          'https://pub-4b74b237794c49bb91bce1ca6a4fc0a2.r2.dev',
          'https://www.palmgardenrs.com/images'
        )

        console.log('🔄 Original R2 URL:', result.url)
        console.log('🔄 Proxy URL:', proxyUrl)
        
        // REMOVED: Problematic verification step that was causing failures
        // The upload API already verifies the upload was successful
        
        uploadedUrls.push(proxyUrl)
        setUploadProgress(((i + 1) / files.length) * 100)

      } catch (err) {
        console.error('❌ Upload error:', err)
        setError(`Failed to upload "${file.name}": ${err.message}`)
      }
    }

    if (uploadedUrls.length > 0) {
      // Add new images to existing ones
      const newImages = [...images, ...uploadedUrls]
      console.log('🖼️ Upload completed, calling onImageUpload with:', newImages);
      onImageUpload(newImages)
    }

    setIsUploading(false)
    setUploadProgress(0)
    
    // Notify parent component about upload state
    if (onUploadStateChange) {
      onUploadStateChange(false);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeImage = (indexToRemove) => {
    console.log('🗑️ Remove image button clicked for index:', indexToRemove);
    const newImages = images.filter((_, index) => index !== indexToRemove)
    onImageUpload(newImages)
  }

  const handleUploadAreaClick = () => {
    if (images.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`)
      return
    }
    fileInputRef.current?.click()
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (images.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`)
      return
    }

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      const event = { target: { files } }
      handleFileSelect(event)
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}

      {/* Current Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={image}
                alt={`Plant image ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border border-forest-200"
                onLoad={() => console.log(`Image ${index + 1} loaded successfully:`, image)}
                onError={(e) => {
                  console.error(`Image ${index + 1} failed to load:`, image)
                  e.target.style.display = 'none'
                }}
              />
              <button
                onClick={() => removeImage(index)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer hover:border-primary-400 hover:bg-primary-50 ${
            isUploading ? 'border-primary-500 bg-primary-50' : 'border-forest-300'
          }`}
          onClick={handleUploadAreaClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 text-forest-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-forest-900 mb-2">
            Upload Plant Images
          </h3>
          <p className="text-forest-600 mb-4">
            Drag and drop images here, or click to browse
          </p>
          <p className="text-sm text-forest-500">
            PNG, JPG, WebP, or GIF up to 5MB each • Maximum {maxImages} images
          </p>
          <p className="text-sm text-forest-500 mt-1">
            {images.length}/{maxImages} images uploaded
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Max Images Reached */}
      {images.length >= maxImages && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm text-center">
            Maximum {maxImages} images reached. Remove an image to add more.
          </p>
        </div>
      )}
    </div>
  )
}

export default ImageUpload 